import { useMemo, type FC } from "react";
import { useMutation } from "@apollo/client";
import { Button, Stack, Tooltip } from "@mui/material";
import ThumbUpOffAltIcon from "@mui/icons-material/ThumbUpOffAlt";
import ThumbDownOffAltIcon from "@mui/icons-material/ThumbDownOffAlt";
import { CreateLikeMutation, DeleteLikeMutation } from "@/entities/contents";
import { useUser } from "@/shared/hooks/use-user/useUser";
import { useTranslation } from "@/shared/hooks";
import type {
  ContentQueryType,
  CreateLikeMutationType,
  DeleteLikeMutationType,
} from "@/types/graphql/site.generated";

type ContentLikes = NonNullable<
  NonNullable<ContentQueryType["content"]>["likes"]
>;

// Порт референсного arena/components/Likes: пара кнопок лайк/дизлайк со
// счётчиком лайков; клик неавторизованного открывает диалог входа. Кэш
// обновляется сам: мутации возвращают ContentLikes с тем же id, что и
// Content.likes (`like-<contentId>`).
export const Likes: FC<{ contentId: string; likes: ContentLikes | null }> = ({
  contentId,
  likes,
}) => {
  const { user, openSignIn } = useUser();
  const { t, i18n } = useTranslation();

  const [createLike] = useMutation<CreateLikeMutationType>(CreateLikeMutation);
  const [deleteLike] = useMutation<DeleteLikeMutationType>(DeleteLikeMutation, {
    variables: { contentId },
  });

  const onClickLike = () => {
    if (!user) return openSignIn();
    if (likes?.liked) {
      void deleteLike();
      return;
    }
    void createLike({ variables: { contentId, type: "LIKE" } });
  };

  const onClickDislike = () => {
    if (!user) return openSignIn();
    if (likes?.disliked) {
      void deleteLike();
      return;
    }
    void createLike({ variables: { contentId, type: "DISLIKE" } });
  };

  // Счётчик форматируется на локали витрины, а не на фиксированной ru-RU:
  // разделители разрядов и compact-суффиксы у языков разные. Форматтер
  // Intl не бесплатный, поэтому пересоздаётся только при смене языка.
  const countFormatter = useMemo(
    () => new Intl.NumberFormat(i18n.language, { notation: "compact" }),
    [i18n.language],
  );
  const count = countFormatter.format(likes?.count ?? 0);

  return (
    <Stack direction="row" spacing={1}>
      <Tooltip
        title={
          likes?.liked ? t("video.actions.unlike") : t("video.actions.like")
        }
      >
        <Button
          variant="outlined"
          color="inherit"
          startIcon={
            <ThumbUpOffAltIcon
              fontSize="small"
              color={likes?.liked ? "primary" : "inherit"}
            />
          }
          size="small"
          onClick={onClickLike}
        >
          {count}
        </Button>
      </Tooltip>
      <Tooltip
        title={
          likes?.disliked
            ? t("video.actions.undislike")
            : t("video.actions.dislike")
        }
      >
        <Button
          variant="outlined"
          color="inherit"
          size="small"
          onClick={onClickDislike}
          sx={{ minWidth: 40, px: 1 }}
        >
          <ThumbDownOffAltIcon
            fontSize="small"
            color={likes?.disliked ? "primary" : "inherit"}
          />
        </Button>
      </Tooltip>
    </Stack>
  );
};
